import { observe, useObserver, viewportWidth } from 'react-ui-observer'
import type { Entry } from 'contentful'
import styled, { css } from 'styled-components'

import type { IPageStoryFields } from 'contentful-shared'
import { breakpoints } from 'bl-common/src/constants/breakpoints'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { Carousel } from 'bl-common/src/units/Carousel/Carousel'
import { Section } from 'bl-common/src/units/Section/Section'
import { media } from 'bl-common/src/utils/media'

import { StoryCard } from './StoryCard'

const CardWrap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const Container = styled.div`
  ${media.md(css`
    display: flex;
  `)}
`

const HeadingWrap = styled.div`
  margin-right: 32px;
  ${media.md(css``)}
`

const ContentWrap = styled.div`
  ${media.md(css`
    width: 75%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 42px;
    margin-left: auto;
  `)}
`

type StoryCarouselProps = { stories: Entry<IPageStoryFields>[] }

const StoryCarousel = ({ stories }: StoryCarouselProps) =>
  stories ? (
    <Carousel renderControls={false} pageMargin={2}>
      {stories.slice(0, 4).map((story, index) => (
        <StoryCard
          key={index}
          imageRatio={0.8}
          title={story.fields.title}
          topic={story.fields.topic}
          date={story.fields.publishDate}
          image={story.fields.heroImage.fields.image}
          link={story.fields.slug}
        />
      ))}
    </Carousel>
  ) : null

type StoryArchiveProps = {
  stories: Entry<IPageStoryFields>[]
  breakOnMobile?: boolean
  label?: string
  top?: React.ComponentProps<typeof Section>['top']
  bottom?: React.ComponentProps<typeof Section>['bottom']
}

export const StoryArchive = ({
  stories,
  breakOnMobile,
  label,
  top = {},
  bottom = {},
}: StoryArchiveProps) => {
  // TODO: Switch to useBreakpoints
  const isMobile = useObserver(
    observe(viewportWidth(), width => width < breakpoints.md)
  )

  return (
    <Section top={top} bottom={bottom}>
      <Container>
        <HeadingWrap>
          <Type size={{ xs: 18, md: 28 }} weight="bold" bottom={{ xs: 2 }}>
            {label}
          </Type>
        </HeadingWrap>
        <ContentWrap>
          {isMobile ? (
            <StoryCarousel stories={stories} />
          ) : (
            stories.map(story => (
              <CardWrap key={story.sys.id}>
                <StoryCard
                  imageRatio={isMobile ? 0.9 : 1.3}
                  title={story.fields.title}
                  topic={story.fields.topic}
                  date={story.fields.publishDate}
                  image={story.fields.heroImage.fields.image}
                  link={story.fields.slug}
                  breakOnMobile={breakOnMobile}
                  isSmall
                />
              </CardWrap>
            ))
          )}
        </ContentWrap>
      </Container>
    </Section>
  )
}
